import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ASSETS, scrollToId } from "../constants";
import { useLanguage } from "../i18n/LanguageContext";

const MaskedLine = ({ children, delay, testId }) => {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        data-testid={testId}
        className="block"
        initial={{ y: reduce ? 0 : "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-svh items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-32">
        <img
          src={ASSETS.heroUrus}
          alt="A&A MOTORS Lamborghini Urus in Dubai at sunset"
          className="h-full w-full object-cover object-center animate-hero-zoom"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-[#08080A]/55 to-[#08080A]/35" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#08080A]/80 via-[#08080A]/25 to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-40 sm:px-6 lg:px-8">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, letterSpacing: "0.6em" }}
          animate={{ opacity: 1, letterSpacing: "0.35em" }}
          transition={{ duration: 1.1, delay: 0.15 }}
          className="mb-5 flex items-center gap-4 text-xs font-semibold uppercase text-gold"
        >
          <span className="inline-block h-px w-10 bg-gold/70" aria-hidden="true" />
          {t("hero.eyebrow")}
        </motion.p>

        <h1 className="font-display font-bold uppercase leading-[0.92] tracking-tight">
          <MaskedLine delay={0.35} testId="hero-line-1">
            <span className="text-7xl text-white drop-shadow-[0_10px_40px_rgba(0,0,0,0.6)] sm:text-8xl lg:text-[10rem]">A&amp;A</span>
          </MaskedLine>
          <MaskedLine delay={0.55} testId="hero-line-2">
            <span className="text-6xl tracking-[0.08em] text-gold-gradient drop-shadow-[0_10px_40px_rgba(0,0,0,0.6)] sm:text-7xl lg:text-[8.5rem]">MOTORS</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-6 text-sm font-bold uppercase tracking-[0.35em] text-gold sm:text-base"
        >
          {t("tagline")}
        </motion.p>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-4 max-w-xl text-base text-slate-300 sm:text-lg"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            type="button"
            data-testid="hero-explore-fleet-button"
            onClick={() => scrollToId("fleet")}
            className="rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#08080A] transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_40px_rgba(212,175,55,0.4)]"
          >
            {t("hero.ctaFleet")}
          </button>
          <button
            type="button"
            data-testid="hero-book-button"
            onClick={() => scrollToId("enquiry")}
            className="rounded-full border border-white/25 bg-white/[0.04] px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white backdrop-blur-md transition-all duration-300 hover:border-gold/60 hover:text-gold"
          >
            {t("hero.ctaBook")}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        type="button"
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("fleet")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-gold/80 sm:flex"
        aria-label={t("hero.scroll")}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.35em]">{t("hero.scroll")}</span>
        <span className="flex h-12 w-6 items-start justify-center rounded-full border border-gold/40 p-1.5">
          <span className="h-2 w-1 rounded-full bg-gold animate-scroll-hint" />
        </span>
      </motion.button>
    </section>
  );
};

export default Hero;
